/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo, useState } from 'react';
import {
  Box,
  Collapse,
  IconButton,
  Table,
  TableCell,
  TableRow,
} from '../../../../components/shared/MaterialUI';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';

import { useUI } from '../../../../app/context/ui';
import AppHelper from '../../../../helpers/AppHelper';
import PayrollService from '../../../../services/newApi/PayrollService';
import EmployeeSubTable from './EmployeeSubTable';

const CompanyRow = ({ company, level, organizationId, currentTab, employeeHandlers, filterModal }) => {
  const [open, setOpen] = useState(false);
  const [children, setChildren] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loadedChildren, setLoadedChildren] = useState(false);
  const [loadedEmployees, setLoadedEmployees] = useState(false);
  const { blockUI, snackbarUI } = useUI();
  const payrollService = useMemo(() => new PayrollService(), []);

  const indent = level * 32;

  const handleToggle = async () => {
    if (!open && !loadedChildren) {
      await fetchChildren();
    }
    setOpen(!open);
  };

  const fetchChildren = async () => {
    try {
      blockUI.current.open(true);
      const response = await payrollService.getCompanies(organizationId, {
        ParentCompanyId: company.id,
        ActiveEmployees: true,
      });
      const data = response?.data?.data || [];
      if (data.length > 0) {
        setChildren(data);
      } else {
        await fetchEmployees();
      }
      setLoadedChildren(true);
      blockUI.current.open(false);
    } catch (error) {
      blockUI.current.open(false);
      AppHelper.checkError(error, snackbarUI);
    }
  };

  const fetchEmployees = async (force = false) => {
    if (loadedEmployees && !force) return;
    const isMaxedOut = currentTab === 2;
    try {
      const filters = { Employeer: company.name };
      if (filterModal?.EmployeeName) filters.EmployeeName = filterModal.EmployeeName;
      const response = await payrollService.getEmployeesActiveTab(
        organizationId,
        { page: 1, size: 100 },
        filters,
        isMaxedOut
      );
      const data = response?.data?.data?.map((item) => ({
        ...item,
        id: item?.employeeId,
      })) || [];
      setEmployees(data);
      setLoadedEmployees(true);
    } catch (error) {
      AppHelper.checkError(error, snackbarUI);
    }
  };

  const refreshEmployees = () => fetchEmployees(true);

  const hasChildren = children.length > 0;
  const isParent = level === 0;

  return (
    <>
      <TableRow
        hover
        onClick={handleToggle}
        sx={{
          cursor: 'pointer',
          backgroundColor: '#fff',
          '&:hover': { backgroundColor: '#f5f5f5' },
          '& > *': { borderBottom: '1px solid #e0e0e0' },
        }}
      >
        <TableCell sx={{ width: 60, paddingLeft: `${indent + 16}px` }}>
          <IconButton size="small">
            <ExpandMoreIcon
              sx={{
                transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s',
                color: '#333',
              }}
            />
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontWeight: isParent ? 600 : 400, fontSize: '0.875rem', color: '#333' }}>
          {company.name || company.displayName}
        </TableCell>
        <TableCell align="center" sx={{ fontSize: '0.875rem', color: '#555' }}>
          {company.activeEmployees ?? ''}
        </TableCell>
      </TableRow>

      {open && (
        <TableRow sx={{ backgroundColor: '#fff' }}>
          <TableCell colSpan={3} sx={{ padding: 0, borderBottom: 'none', backgroundColor: '#fff' }}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              {hasChildren ? (
                <Box sx={{ width: '100%' }}>
                  <Table size="small">
                    <tbody>
                      {children.map((child) => (
                        <CompanyRow
                          key={child.id}
                          company={child}
                          level={level + 1}
                          organizationId={organizationId}
                          currentTab={currentTab}
                          employeeHandlers={employeeHandlers}
                          filterModal={filterModal}
                        />
                      ))}
                    </tbody>
                  </Table>
                </Box>
              ) : (
                <EmployeeSubTable
                  employees={employees}
                  currentTab={currentTab}
                  employeeHandlers={employeeHandlers}
                  indent={indent + 32}
                  onRefresh={refreshEmployees}
                />
              )}
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

export default CompanyRow;
